import {
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import type { FirebaseError } from "firebase/app";
import {
  useCallback,
  useState,
  type ChangeEvent,
} from "react";
import { toast } from "react-toastify";
import {
  useRutasActions,
  useAuthState,
  useRutasState,
} from "../../../context/AppContext";
import { auth } from "../../../firebase/firebaseConfig";
import type {
  ModalCambiarContrasenaProps,
  ModalCambiarContrasenaUsuarioRuta,
  PasswordFieldKey,
  PasswordVisibilityKey,
  UseModalCambiarContrasenaReturn,
} from "./types";

const obtenerMensajeError = (error: unknown): string => {
  const firebaseError = error as FirebaseError | undefined;

  switch (firebaseError?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
      return "contrasena actual incorrecta";
    case "auth/weak-password":
      return "La nueva contrasena es demasiado debil";
    case "auth/too-many-requests":
      return "Demasiados intentos. Intenta nuevamente en unos minutos";
    default:
      return error instanceof Error ? error.message : String(error);
  }
};

const validarFormulario = (
  currentPassword: string,
  newPassword: string,
  newPasswordVerify: string
): string | null => {
  if (!currentPassword.trim() || !newPassword.trim() || !newPasswordVerify.trim()) {
    return "Completa todos los campos";
  }

  if (newPassword !== newPasswordVerify) {
    return "Las contrasenas nuevas no coinciden";
  }

  if (currentPassword === newPassword) {
    return "La nueva contrasena debe ser diferente a la actual";
  }

  return null;
};

const updatePasswordCollection = async (
  collectionName: "adminPass" | "rutasPass",
  userUid: string,
  newPassword: string
): Promise<void> => {
  const db = getFirestore();
  const passwordQuery = query(
    collection(db, collectionName),
    where("uid", "==", userUid)
  );
  const passwordSnapshot = await getDocs(passwordQuery);

  if (!passwordSnapshot.docs.length) {
    console.error(
      `No se encontro el documento en ${collectionName} asociado al usuario actual`
    );
    return;
  }

  await updateDoc(passwordSnapshot.docs[0].ref, {
    contrasena: newPassword,
  });
};

const bloquearRuta = async (
  usuarioRuta: ModalCambiarContrasenaUsuarioRuta | null
): Promise<void> => {
  if (!usuarioRuta?.adminUid || !usuarioRuta?.uid) {
    return;
  }

  const db = getFirestore();
  const rutaRef = doc(
    db,
    "admin_users",
    usuarioRuta.adminUid,
    "rutas",
    usuarioRuta.uid
  );

  await setDoc(
    rutaRef,
    {
      isAutorized: false,
    },
    { merge: true }
  );
};

export const useModalCambiarContrasena = ({
  setverCambiarContrasenaModal,
}: ModalCambiarContrasenaProps): UseModalCambiarContrasenaReturn => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordVerify, setNewPasswordVerify] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isButtonPassword, setIsButtonPassword] = useState(true);
  const [isButtonPasswordNew, setIsButtonPasswordNew] = useState(true);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { userData } = useAuthState();
  const { usuarioRuta } = useRutasState();
  const { setUsuarioRuta } = useRutasActions();
  const usuarioRutaNormalizada =
    (usuarioRuta as ModalCambiarContrasenaUsuarioRuta | null) ?? null;

  const resetPasswords = useCallback(() => {
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordVerify("");
  }, []);

  const handleClose = useCallback(() => {
    setverCambiarContrasenaModal(false);
  }, [setverCambiarContrasenaModal]);

  const togglePasswordVisibility = useCallback((field: PasswordVisibilityKey) => {
    if (field === "current") {
      setIsButtonPassword((prevState) => !prevState);
      return;
    }

    setIsButtonPasswordNew((prevState) => !prevState);
  }, []);

  const handlePasswordFieldChange = useCallback(
    (field: PasswordFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setError(null);

      if (field === "currentPassword") {
        setCurrentPassword(value);
        return;
      }

      if (field === "newPassword") {
        setNewPassword(value);
        return;
      }

      setNewPasswordVerify(value);
    },
    []
  );

  const handleChangePassword = useCallback(async () => {
    if (isSubmiting) {
      return;
    }

    const errorValidacion = validarFormulario(
      currentPassword,
      newPassword,
      newPasswordVerify
    );

    if (errorValidacion) {
      setError(errorValidacion);
      toast.error(errorValidacion);
      return;
    }

    setIsSubmiting(true);
    setError(null);

    try {
      const user = auth.currentUser;

      if (!user) {
        throw new Error("Usuario no autenticado");
      }

      const credentials = EmailAuthProvider.credential(
        user.email ?? "",
        currentPassword
      );

      await reauthenticateWithCredential(user, credentials);

      await updatePassword(user, newPassword);

      if (!userData) {
        await updatePasswordCollection("rutasPass", user.uid, newPassword);
        await bloquearRuta(usuarioRutaNormalizada);
        setUsuarioRuta((previousUsuarioRuta) =>
          previousUsuarioRuta
            ? { ...previousUsuarioRuta, isAutorized: false }
            : previousUsuarioRuta
        );

        toast.success("Contrasena cambiada exitosamente");
        setverCambiarContrasenaModal(false);
        return;
      }

      await updatePasswordCollection("adminPass", user.uid, newPassword);

      toast.success("Contrasena cambiada exitosamente");
      setverCambiarContrasenaModal(false);
    } catch (changePasswordError) {
      const errorMessage = obtenerMensajeError(changePasswordError);

      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error al cambiar la contrasena", changePasswordError);
      resetPasswords();
    } finally {
      setIsSubmiting(false);
    }
  }, [
    currentPassword,
    isSubmiting,
    newPassword,
    newPasswordVerify,
    resetPasswords,
    setverCambiarContrasenaModal,
    setUsuarioRuta,
    userData,
    usuarioRutaNormalizada,
  ]);

  return {
    currentPassword,
    error,
    handleChangePassword,
    handleClose,
    handlePasswordFieldChange,
    isButtonPassword,
    isButtonPasswordNew,
    isSubmiting,
    newPassword,
    newPasswordVerify,
    togglePasswordVisibility,
  };
};
